import type { SelectionEvents } from '@fluentui/react-components';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import type { StoredSelectedItemsSchema } from '../../../types.js';
import type { DateItem, useGetDateFilterData } from './data.js';

type UseHandleSelectionChangeProps = {
    data: ReturnType<typeof useGetDateFilterData>;
    selectedItems: StoredSelectedItemsSchema;
    setSelectedItems: Dispatch<SetStateAction<StoredSelectedItemsSchema>>;
};

export function useOnDateSelectionChange({ data, selectedItems, setSelectedItems }: UseHandleSelectionChangeProps) {
    const handleSelectionChange = useCallback(
        (_: SelectionEvents, changeData: { selectedOptions: string[] }) => {
            const newSelection = new Set(selectedItems.current);
            const changedItems = new Set(
                changeData.selectedOptions
                    .filter((id) => !selectedItems.current.includes(id))
                    .concat(selectedItems.current.filter((id) => !changeData.selectedOptions.includes(id)))
            );

            // handle "Select All" special case
            if (changedItems.has('select-all')) {
                const isSelectingAll = changeData.selectedOptions.includes('select-all');

                if (isSelectingAll) {
                    // select everything
                    data.allIds.forEach((id) => newSelection.add(id));
                } else {
                    // deselect everything
                    newSelection.clear();
                }

                setSelectedItems({
                    ...selectedItems,
                    current: Array.from(newSelection),
                });

                return;
            }

            // process each changed item
            changedItems.forEach((changedId) => {
                const item = data.items.find((i) => i.id === changedId);

                if (!item) {
                    return;
                }

                const isSelecting = changeData.selectedOptions.includes(changedId);
                const affectedIds = recursiveGetChildIds(item);

                if (isSelecting) {
                    // add all child items and the item itself
                    affectedIds.forEach((id) => newSelection.add(id));

                    // check if all siblings are selected to select parent
                    const parentIds = getParentIds(data, changedId);
                    parentIds.forEach((parentId) => {
                        const parent = data.items.find((i) => i.id === parentId);

                        if (parent?.children) {
                            const allChildrenSelected = parent.children.every((child) =>
                                recursiveGetChildIds(child).every((id) => newSelection.has(id))
                            );

                            if (allChildrenSelected) {
                                newSelection.add(parentId);
                            }
                        }
                    });

                    // check if everything is selected to select "Select All"
                    const allSelected = data.allIds.every((id) => id === 'select-all' || newSelection.has(id));
                    if (allSelected) {
                        newSelection.add('select-all');
                    }
                } else {
                    // remove all child items and the item itself
                    affectedIds.forEach((id) => newSelection.delete(id));

                    // remove parent selections
                    const parentIds = getParentIds(data, changedId);
                    parentIds.forEach((id) => newSelection.delete(id));

                    // always remove "Select All" when deselecting anything
                    newSelection.delete('select-all');
                }
            });

            setSelectedItems({
                ...selectedItems,
                current: Array.from(newSelection),
            });
        },
        [data, selectedItems, setSelectedItems]
    );

    return {
        handleSelectionChange,
    };
}

// utility to get parent IDs
function getParentIds<TData, TValue>(
    dateData: ReturnType<typeof useGetDateFilterData<TData, TValue>>,
    itemId: string
): string[] {
    const ids: string[] = [];

    const recursivelyFindParent = (items: DateItem[], targetId: string) => {
        for (const item of items) {
            if (!item!.children) {
                continue;
            }

            const isParent = item.children.some((child) => {
                return child.id === targetId || recursiveGetChildIds(child).includes(targetId);
            });

            if (isParent) {
                ids.push(item.id);
                recursivelyFindParent(dateData.hierarchy, item.id);
            }

            recursivelyFindParent(item.children, targetId);
        }
    };

    recursivelyFindParent(dateData.hierarchy, itemId);

    return ids;
}

// utility to recursively get all child ids
function recursiveGetChildIds(item: DateItem) {
    const ids = [item.id];

    if (item.children) {
        item.children.forEach((child) => {
            ids.push(...recursiveGetChildIds(child));
        });
    }

    return ids;
}
